import { useEffect, useRef, useState } from 'react';
import { refreshMentionHtml } from '../../../utils/mentionUtils';
import { useVariableRefresh } from '../../../hooks/useVariableRefresh';

/**
 * Local paragraph HTML plus the two self-heal passes: mention titles/icons
 * and variable pill values. External tree writes (merge, undo) win over
 * keystrokes only when they actually differ.
 */
export const useTextBlockContent = ({
  block,
  onUpdate,
  resolveDocMeta,
  resolveVariable,
}) => {
  const [content, setContent] = useState(block.content || '');
  const contentEditableRef = useRef(null);
  const contentRef = useRef(content);

  // Sync EXTERNAL tree changes (e.g. a merge writing into this block) into
  // the local state. Own keystrokes round-trip equal, so typing is untouched.
  useEffect(() => {
    if ((block.content || '') !== contentRef.current) {
      contentRef.current = block.content || '';
      setContent(block.content || '');
    }
  }, [block.content]);

  // Mention tags always show each doc's CURRENT title/icon (10b): when the
  // library metadata arrives (or the content changes), rewrite stale tags and
  // commit so the stored tree self-heals. Unknown ids are left as stored.
  useEffect(() => {
    if (!resolveDocMeta) return;
    const refreshed = refreshMentionHtml(contentRef.current, resolveDocMeta);
    if (refreshed !== contentRef.current) {
      contentRef.current = refreshed;
      setContent(refreshed);
      onUpdate(block.id, { content: refreshed });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolveDocMeta, block.content, block.id]);

  // Variable pills always show the CURRENT context's values (same self-heal
  // pattern as mention titles — see useVariableRefresh)
  useVariableRefresh({
    block,
    contentRef,
    setContent,
    onUpdate,
    resolveVariable,
    contentEditableRef,
  });

  return { content, setContent, contentRef, contentEditableRef };
};
